import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useOrganizationCheckOutletContext } from "../hooks/useOrganizationCheckOutletContext";
import { CheckRemediation } from "~/pages/space/security/components/Check/CheckRemediation/CheckRemediation";
import { CheckQueriesMql } from "~/pages/space/security/components/Check/CheckQueries/components/CheckQueriesMql";

export function OrganizationCheckRemediationTab() {
  const {
    check,
    variants,
    selectedVariant,
    hasVariants,
    searchParams,
    setSearchParams,
  } = useOrganizationCheckOutletContext();

  const variantQueries = check?.variantQueries?.flatMap((vq) => vq ?? []) || [];
  const selectedVariantQuery = variantQueries.find(
    (vq) => vq.filterID === selectedVariant?.value,
  );

  const mql = selectedVariantQuery?.mql || check?.mql;
  const remediations =
    selectedVariantQuery?.docs?.remediations || check?.docs?.remediations;
  const hasRemediations =
    remediations?.__typename === "Remediations" &&
    remediations.entries.length > 0;

  const hasContent = mql || hasRemediations;

  const handleVariantChange = (event: SelectChangeEvent) => {
    const variantId = event.target.value;
    if (variantId.length > 0) {
      searchParams.set("variantQuery", variantId);
    } else {
      searchParams.delete("variantQuery");
    }
    setSearchParams(searchParams);
  };

  if (!hasContent) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No remediation information available for this check.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {hasVariants && variants.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 3 }}>
            Select check variant type to review relevant remediation.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="variant-select-label">Platform</InputLabel>
            <Select
              labelId="variant-select-label"
              id="variant-select"
              value={selectedVariant?.value || ""}
              label="Variant"
              onChange={handleVariantChange}
              sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
              }}
            >
              {variants.map((variant) => (
                <MenuItem key={variant.value} value={variant.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {variant.icon}
                    {variant.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {hasRemediations && <CheckRemediation entries={remediations.entries} />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 700,
            mr: 1,
            fontSize: "24px",
            lineHeight: "28px",
            color: "text.primary",
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
          }}
        >
          Queries
        </Typography>
        <Divider
          flexItem={true}
          orientation="horizontal"
          sx={{ ml: 1.5, width: "100%", flexShrink: 1, height: 14 }}
        />
      </Box>
      {mql && (
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={700} sx={{ mb: 2 }}>
            MQL
          </Typography>
          <CheckQueriesMql mql={mql} />
        </Box>
      )}
    </Box>
  );
}
